import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRightOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  ExportOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Button, Card, Descriptions, message, Space, Spin, Steps, Typography } from "antd";
import {
  ProFormDigit,
  ProFormRadio,
  ProFormSelect,
  ProFormText,
  StepsForm,
  type ProFormInstance,
} from "@ant-design/pro-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { attachSamplePack, createComplianceCase, getComplianceCase, validateComplianceCase } from "../api";
import { buildComplianceCaseCreatePayload } from "./buildCaseCreatePayload";
import { AUTHOR_PARTY_ROLE_VALUES, documentsMinReady } from "./complianceLabels";
import type {
  AuthorPartyRole,
  Commodity,
  ComplianceCaseCreate,
  ComplianceCaseDetail,
  DeforestationAssessment,
} from "./types";
import { ProFormCountrySelect } from "../components/CountrySelect";
import { countryLabel, ensureCountriesLoaded } from "../countries/countryUtils";
import { formatApiErrorDetail } from "../utils/apiError";
import { DocumentUploadStep } from "./components/DocumentUploadStep";
import { ValidationPanel } from "./components/ValidationPanel";
import { ResolutionPanel } from "./components/ResolutionPanel";
import { PassportPanel } from "./components/PassportPanel";
import { SharePanel } from "./components/SharePanel";
import { TIMBER_DE_DEMO_PRESET } from "./ux/demoPreset";
import { pushNotification } from "./ux/notificationStore";
const AUTHOR_PARTY_ROLE_ICONS: Record<AuthorPartyRole, React.ReactNode> = {
  importer_operator: <SafetyCertificateOutlined />,
  trader_exporter: <ExportOutlined />,
  processor_buyer: <ShopOutlined />,
  supplier_producer: <EnvironmentOutlined />,
  compliance_broker: <TeamOutlined />,
};

const CASE_SUBSTEP_KEYS = ["commodity", "supplier", "buyer", "shipment", "destination", "review"] as const;
type CaseSubstepKey = (typeof CASE_SUBSTEP_KEYS)[number];

const CASE_SUBSTEP_FIELD_NAMES: (keyof ComplianceCaseCreate)[][] = [
  ["commodity"],
  ["supplier_name", "supplier_country"],
  ["buyer_name", "buyer_country"],
  [],
  ["destination_country"],
  [],
];

/** All fields sent on create; used so values survive unmounted StepsForm / sub-steps. */
const CASE_CREATE_FIELD_NAMES: (keyof ComplianceCaseCreate)[] = [
  "author_party_role",
  "author_role_detail",
  "client_org_name",
  "commodity",
  "supplier_name",
  "supplier_country",
  "supplier_contact",
  "buyer_name",
  "buyer_country",
  "contract_reference",
  "shipment_volume",
  "shipment_unit",
  "destination_country",
];

const LAST_CASE_SUBSTEP = CASE_SUBSTEP_KEYS.length - 1;

const WIZARD_STEP = {
  yourRole: 0,
  caseData: 1,
  documents: 2,
  validate: 3,
  invite: 4,
  passport: 5,
  share: 6,
} as const;

const LAST_WIZARD_STEP = WIZARD_STEP.share;

function commodityLabel(t: (key: string) => string, value?: Commodity) {
  if (!value) return "";
  return t(`compliance.newCase.${value}`);
}

/** Merge known create fields from form/store patches into the durable draft. */
function mergeCaseDraft(
  prev: Partial<ComplianceCaseCreate>,
  patch: Record<string, unknown>
): Partial<ComplianceCaseCreate> {
  const next: Partial<ComplianceCaseCreate> = { ...prev };
  for (const key of CASE_CREATE_FIELD_NAMES) {
    if (Object.prototype.hasOwnProperty.call(patch, key) && patch[key] !== undefined) {
      (next as Record<string, unknown>)[key] = patch[key];
    }
  }
  return next;
}

export function NewCasePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef<ProFormInstance<ComplianceCaseCreate>>();
  const [wizardStep, setWizardStep] = useState(0);
  const [caseSubStep, setCaseSubStep] = useState(0);
  const [createdCaseId, setCreatedCaseId] = useState<string | null>(null);
  const [caseData, setCaseData] = useState<ComplianceCaseDetail | null>(null);
  const [caseLoading, setCaseLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [authorPartyRole, setAuthorPartyRole] = useState<AuthorPartyRole>("supplier_producer");
  /**
   * Durable create-field store. StepsForm keeps a separate form per wizard step, and case-data
   * sub-steps unmount fields — so setFieldsValue on formRef alone (often still the role step)
   * never reaches create/review. Draft is the source of truth for review + API payload.
   */
  const [caseDraft, setCaseDraft] = useState<Partial<ComplianceCaseCreate>>({});
  const caseDraftRef = useRef(caseDraft);
  caseDraftRef.current = caseDraft;

  const syncDraftToCaseForm = useCallback(() => {
    if (wizardStep !== WIZARD_STEP.caseData) return;
    const draft = caseDraftRef.current;
    if (Object.keys(draft).length === 0) return;
    formRef.current?.setFieldsValue?.(draft);
  }, [wizardStep]);

  const applyDemoPresetFields = useCallback(() => {
    setCaseDraft({ ...TIMBER_DE_DEMO_PRESET });
    setAuthorPartyRole(TIMBER_DE_DEMO_PRESET.author_party_role);
    setWizardStep(WIZARD_STEP.caseData);
    setCaseSubStep(LAST_CASE_SUBSTEP);
  }, []);

  const runOneClickDemo = async () => {
    setDemoLoading(true);
    try {
      const created = await createComplianceCase(TIMBER_DE_DEMO_PRESET);
      try {
        await attachSamplePack(created.id);
      } catch {
        // optional geo/docs
      }
      setCreatedCaseId(created.id);
      setCaseData(created);
      message.success(t("compliance.demoPreset.created"));
      pushNotification({
        kind: "passport_generated",
        title: t("compliance.demoPreset.created"),
        caseId: created.id,
      });
      setWizardStep(WIZARD_STEP.documents);
    } catch (err) {
      const detail = formatApiErrorDetail(err);
      message.error(
        detail ? `${t("compliance.demoPreset.failed")}: ${detail}` : t("compliance.demoPreset.failed")
      );
    } finally {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("preset") === "timber-de") {
      // Defer until StepsForm mounts; draft sync runs after caseData step is current.
      const tmr = window.setTimeout(() => applyDemoPresetFields(), 0);
      return () => window.clearTimeout(tmr);
    }
  }, [searchParams, applyDemoPresetFields]);

  // After jumping to caseData (preset / fill button), push draft into that step's form instance.
  useEffect(() => {
    if (wizardStep !== WIZARD_STEP.caseData) return;
    const tmr = window.setTimeout(() => syncDraftToCaseForm(), 0);
    return () => window.clearTimeout(tmr);
  }, [wizardStep, caseSubStep, caseDraft, syncDraftToCaseForm]);

  const reloadCase = useCallback(async () => {
    if (!createdCaseId) return;
    setCaseLoading(true);
    try {
      setCaseData(await getComplianceCase(createdCaseId));
    } catch {
      message.error(t("compliance.caseDetail.loadFailed"));
    } finally {
      setCaseLoading(false);
    }
  }, [createdCaseId, t]);

  const handleWizardStepChange = (next: number) => {
    if (next > WIZARD_STEP.caseData && !createdCaseId) return;
    if (next === WIZARD_STEP.yourRole) {
      setCaseSubStep(0);
    }
    setWizardStep(next);
  };

  const validateCaseSubstep = async () => {
    const fields = CASE_SUBSTEP_FIELD_NAMES[caseSubStep];
    if (fields.length === 0) return true;
    try {
      await formRef.current?.validateFields?.(fields);
      return true;
    } catch {
      return false;
    }
  };

  const createCaseAndContinue = async () => {
    if (!(await validateCaseSubstep())) return;
    try {
      await ensureCountriesLoaded();
    } catch {
      message.error(t("compliance.newCase.createFailed"));
      return;
    }
    // Prefer draft (survives StepsForm step swap + unmounted sub-steps); form overlays only as fallback.
    const stored = formRef.current?.getFieldsValue?.(CASE_CREATE_FIELD_NAMES) as
      | Record<string, unknown>
      | undefined;
    const roleFromForm = formRef.current?.getFieldValue?.("author_party_role") as
      | AuthorPartyRole
      | undefined;
    const raw: Record<string, unknown> = {
      ...(stored ?? {}),
      ...caseDraftRef.current,
      author_party_role:
        caseDraftRef.current.author_party_role ?? roleFromForm ?? authorPartyRole,
    };
    let payload: ComplianceCaseCreate;
    try {
      payload = buildComplianceCaseCreatePayload(raw);
    } catch {
      message.error(t("compliance.newCase.createFailed"));
      return;
    }
    setCreating(true);
    try {
      const created = await createComplianceCase(payload);
      setCreatedCaseId(created.id);
      setCaseData(created);
      message.success(t("compliance.newCase.created"));
      setWizardStep(WIZARD_STEP.documents);
    } catch (err) {
      const detail = formatApiErrorDetail(err);
      message.error(
        detail ? `${t("compliance.newCase.createFailed")}: ${detail}` : t("compliance.newCase.createFailed")
      );
    } finally {
      setCreating(false);
    }
  };

  const caseSubstepItems = CASE_SUBSTEP_KEYS.map((key) => ({
    title: t(`compliance.newCase.caseData.substeps.${key}`),
  }));

  const renderReview = () => {
    const formValues = (formRef.current?.getFieldsValue?.(true) ?? {}) as Record<string, unknown>;
    // Draft wins: form store is often empty on review because sub-step fields are unmounted / wrong step.
    const values = { ...formValues, ...caseDraft };
    const role = (values.author_party_role as AuthorPartyRole | undefined) ?? authorPartyRole;
    return (
      <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
        {role && (
          <Descriptions.Item label={t("compliance.case.authorPartyRole.summary")}>
            {t(`compliance.case.authorPartyRole.${role}`)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("compliance.newCase.commodity")}>
          {commodityLabel(t, values.commodity as Commodity | undefined)}
        </Descriptions.Item>
        <Descriptions.Item label={t("compliance.newCase.supplierName")}>{values.supplier_name}</Descriptions.Item>
        <Descriptions.Item label={t("compliance.newCase.supplierCountry")}>
          {countryLabel(values.supplier_country, i18n.language)}
        </Descriptions.Item>
        {values.supplier_contact && (
          <Descriptions.Item label={t("compliance.newCase.supplierContact")}>
            {values.supplier_contact}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("compliance.newCase.buyerName")}>{values.buyer_name}</Descriptions.Item>
        <Descriptions.Item label={t("compliance.newCase.buyerCountry")}>
          {countryLabel(values.buyer_country, i18n.language)}
        </Descriptions.Item>
        {values.contract_reference && (
          <Descriptions.Item label={t("compliance.newCase.contractReference")}>
            {values.contract_reference}
          </Descriptions.Item>
        )}
        {values.client_org_name && (
          <Descriptions.Item label={t("compliance.newCase.clientOrgName")}>
            {values.client_org_name}
          </Descriptions.Item>
        )}
        {(values.shipment_volume != null || values.shipment_unit) && (
          <Descriptions.Item label={t("compliance.newCase.shipmentVolume")}>
            {[values.shipment_volume, values.shipment_unit].filter(Boolean).join(" ")}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("compliance.newCase.destinationCountry")}>
          {countryLabel(values.destination_country, i18n.language)}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const renderCaseSubstepFields = (key: CaseSubstepKey) => {
    switch (key) {
      case "commodity":
        return (
          <ProFormSelect
            name="commodity"
            label={t("compliance.newCase.commodity")}
            rules={[{ required: true }]}
            options={[
              { label: t("compliance.newCase.timber"), value: "timber" },
              { label: t("compliance.newCase.palm_oil"), value: "palm_oil" },
              { label: t("compliance.newCase.coffee"), value: "coffee" },
              { label: t("compliance.newCase.cocoa"), value: "cocoa" },
              { label: t("compliance.newCase.soy"), value: "soy" },
              { label: t("compliance.newCase.rubber"), value: "rubber" },
            ]}
          />
        );
      case "supplier":
        return (
          <>
            <ProFormText
              name="supplier_name"
              label={t("compliance.newCase.supplierName")}
              rules={[{ required: true }]}
            />
            <ProFormCountrySelect
              name="supplier_country"
              label={t("compliance.newCase.supplierCountry")}
              rules={[{ required: true }]}
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
              {t("compliance.newCase.criticalCountryHint")}
            </Typography.Paragraph>
            <ProFormText name="supplier_contact" label={t("compliance.newCase.supplierContact")} />
          </>
        );
      case "buyer":
        return (
          <>
            <ProFormText name="buyer_name" label={t("compliance.newCase.buyerName")} rules={[{ required: true }]} />
            <ProFormCountrySelect
              name="buyer_country"
              label={t("compliance.newCase.buyerCountry")}
              rules={[{ required: true }]}
            />
          </>
        );
      case "shipment":
        return (
          <>
            <ProFormText
              name="client_org_name"
              label={t("compliance.newCase.clientOrgName")}
              placeholder={t("compliance.newCase.clientOrgPlaceholder")}
              extra={t("compliance.newCase.clientOrgHint")}
            />
            <ProFormText name="contract_reference" label={t("compliance.newCase.contractReference")} />
            <ProFormDigit name="shipment_volume" label={t("compliance.newCase.shipmentVolume")} min={0} />
            <ProFormText
              name="shipment_unit"
              label={t("compliance.newCase.shipmentUnit")}
              placeholder={t("compliance.newCase.shipmentUnitPlaceholder")}
            />
          </>
        );
      case "destination":
        return (
          <ProFormCountrySelect
            name="destination_country"
            label={t("compliance.newCase.destinationCountry")}
            rules={[{ required: true }]}
          />
        );
      case "review":
        return (
          <>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              {t("compliance.newCase.caseData.reviewHint")}
            </Typography.Paragraph>
            {renderReview()}
          </>
        );
      default:
        return null;
    }
  };

  const renderWorkflowBody = () => {
    if (!caseData) {
      return <Typography.Text type="secondary">{t("compliance.newCase.workflow.caseRequired")}</Typography.Text>;
    }
    return (
      <Spin spinning={caseLoading}>
        {wizardStep === WIZARD_STEP.documents && (
          <DocumentUploadStep caseData={caseData} onUpdated={reloadCase} />
        )}
        {wizardStep === WIZARD_STEP.validate && (
          <>
            <Button
              type="primary"
              loading={validating}
              onClick={async () => {
                setValidating(true);
                try {
                  await validateComplianceCase(caseData.id);
                  message.success(t("compliance.caseDetail.validationComplete"));
                  await reloadCase();
                } catch {
                  message.error(t("compliance.caseDetail.validationFailed"));
                } finally {
                  setValidating(false);
                }
              }}
              style={{ marginBottom: 16 }}
            >
              {t("compliance.caseDetail.runValidation")}
            </Button>
            <ValidationPanel
              validation={caseData.latest_validation}
              loading={validating}
              deforestation={
                (caseData.validation_summary as { deforestation?: DeforestationAssessment } | null)
                  ?.deforestation ?? null
              }
            />
          </>
        )}
        {wizardStep === WIZARD_STEP.invite && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 0 }}>
              {t("compliance.newCase.workflow.inviteHint")}
            </Typography.Paragraph>
            <ResolutionPanel
              caseData={caseData}
              onUpdated={reloadCase}
              validating={validating}
              setValidating={setValidating}
            />
          </Space>
        )}
        {wizardStep === WIZARD_STEP.passport && <PassportPanel caseData={caseData} onUpdated={reloadCase} />}
        {wizardStep === WIZARD_STEP.share && <SharePanel caseData={caseData} onUpdated={reloadCase} />}
      </Spin>
    );
  };

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <PlusOutlined style={{ fontSize: 20, color: "#0b6cff" }} aria-hidden />
          {t("compliance.newCase.title")}
        </span>
      }
      extra={
        !createdCaseId ? (
          <Space wrap>
            <Button
              icon={<ExperimentOutlined />}
              onClick={applyDemoPresetFields}
              data-testid="demo-preset-fill"
            >
              {t("compliance.demoPreset.fillForm")}
            </Button>
            <Button
              type="primary"
              ghost
              icon={<ExperimentOutlined />}
              loading={demoLoading}
              onClick={runOneClickDemo}
              data-testid="demo-preset-one-click"
            >
              {t("compliance.demoPreset.button")}
            </Button>
          </Space>
        ) : null
      }
    >
      <StepsForm<ComplianceCaseCreate>
        formRef={formRef}
        current={wizardStep}
        onCurrentChange={handleWizardStepChange}
        containerStyle={{ width: "100%" }}
        onFinish={async () => {
          if (createdCaseId) {
            navigate(`/dds/cases/${createdCaseId}`);
          }
          return true;
        }}
        formProps={{ layout: "vertical", preserve: true }}
        submitter={{
          render: (props) => {
            if (props.step === WIZARD_STEP.yourRole) {
              return null;
            }
            if (props.step === WIZARD_STEP.caseData) {
              const isLastCaseSubstep = caseSubStep >= LAST_CASE_SUBSTEP;
              return (
                <Space>
                  {caseSubStep > 0 && (
                    <Button onClick={() => setCaseSubStep((s) => s - 1)}>{t("common.back")}</Button>
                  )}
                  {!isLastCaseSubstep ? (
                    <Button
                      type="primary"
                      onClick={async () => {
                        if (!(await validateCaseSubstep())) return;
                        setCaseSubStep((s) => s + 1);
                      }}
                    >
                      {t("common.next")}
                    </Button>
                  ) : (
                    <Button type="primary" loading={creating} onClick={createCaseAndContinue}>
                      {t("compliance.newCase.submit")}
                    </Button>
                  )}
                </Space>
              );
            }
            if (!createdCaseId) {
              return null;
            }
            const isLast = props.step >= LAST_WIZARD_STEP;
            const hasBlockers = caseData?.latest_validation && !caseData.latest_validation.passed;
            return (
              <Space direction="vertical" style={{ width: "100%", alignItems: "center" }}>
                <Space>
                  <Button disabled={props.step <= WIZARD_STEP.documents} onClick={() => setWizardStep((s) => s - 1)}>
                    {t("common.back")}
                  </Button>
                  {!isLast ? (
                    <Button
                      type="primary"
                      disabled={
                        props.step === WIZARD_STEP.documents &&
                        caseData != null &&
                        !documentsMinReady(caseData.documents)
                      }
                      onClick={() => setWizardStep((s) => s + 1)}
                    >
                      {t("common.next")}
                    </Button>
                  ) : (
                    <Button type="primary" onClick={() => props.onSubmit?.()}>
                      {t("compliance.newCase.workflow.finish")}
                    </Button>
                  )}
                </Space>
                {hasBlockers && props.step < WIZARD_STEP.invite && (
                  <Typography.Text type="danger">{t("compliance.caseDetail.blockersHint")}</Typography.Text>
                )}
              </Space>
            );
          },
        }}
        stepsProps={{
          size: "small",
        }}
      >
        <StepsForm.StepForm
          name="yourRole"
          title={t("compliance.newCase.yourRole.stepTitle")}
          initialValues={{ author_party_role: "supplier_producer" }}
          onValuesChange={(_, all) => {
            const role = all.author_party_role as AuthorPartyRole | undefined;
            if (role) {
              setAuthorPartyRole(role);
              setCaseDraft((prev) => ({ ...prev, author_party_role: role }));
            }
          }}
        >
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {t("compliance.newCase.yourRole.title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary">{t("compliance.newCase.yourRole.subtitle")}</Typography.Paragraph>
          <ProFormRadio.Group
            name="author_party_role"
            rules={[{ required: true }]}
            fieldProps={{ style: { display: "flex", flexDirection: "column", gap: 8 } }}
            options={AUTHOR_PARTY_ROLE_VALUES.map((value) => ({
              value,
              label: (
                <div style={{ display: "flex", gap: 10 }}>
                  <span
                    aria-hidden
                    style={{
                      fontSize: 16,
                      color: "#0b6cff",
                      lineHeight: 1,
                      flexShrink: 0,
                      display: "inline-flex",
                      alignSelf: "center",
                    }}
                  >
                    {AUTHOR_PARTY_ROLE_ICONS[value]}
                  </span>
                  <div>
                    <Typography.Text strong>{t(`compliance.newCase.yourRole.${value}.title`)}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary">
                      {t(`compliance.newCase.yourRole.${value}.description`)}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t(`compliance.newCase.yourRole.${value}.valueProp`)}
                    </Typography.Text>
                  </div>
                </div>
              ),
            }))}
          />
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 16 }}>
            {t("compliance.newCase.yourRole.inviteHint")}
          </Typography.Paragraph>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={async () => {
                try {
                  await formRef.current?.validateFields?.(["author_party_role"]);
                  const role = formRef.current?.getFieldValue?.("author_party_role") as
                    | AuthorPartyRole
                    | undefined;
                  if (role) {
                    setAuthorPartyRole(role);
                    setCaseDraft((prev) => ({ ...prev, author_party_role: role }));
                  }
                  setWizardStep(WIZARD_STEP.caseData);
                } catch {
                  /* validation message shown by form */
                }
              }}
            >
              {t("common.next")}
            </Button>
          </div>
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="caseData"
          title={t("compliance.newCase.caseData.stepTitle")}
          onValuesChange={(changed) => {
            setCaseDraft((prev) => mergeCaseDraft(prev, changed as Record<string, unknown>));
          }}
        >
          <Steps
            current={caseSubStep}
            size="small"
            style={{ marginBottom: 24 }}
            items={caseSubstepItems}
            onChange={async (next) => {
              if (next < caseSubStep) {
                setCaseSubStep(next);
                return;
              }
              for (let i = caseSubStep; i < next; i += 1) {
                const fields = CASE_SUBSTEP_FIELD_NAMES[i];
                if (fields.length === 0) continue;
                try {
                  await formRef.current?.validateFields?.(fields);
                } catch {
                  setCaseSubStep(i);
                  return;
                }
              }
              setCaseSubStep(next);
            }}
          />
          {renderCaseSubstepFields(CASE_SUBSTEP_KEYS[caseSubStep])}
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="documents"
          title={t("compliance.caseDetail.steps.documents")}
          onFinish={async () => true}
        >
          {renderWorkflowBody()}
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="validate"
          title={t("compliance.caseDetail.steps.validate")}
          onFinish={async () => true}
        >
          {renderWorkflowBody()}
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="invite"
          title={t("compliance.newCase.workflow.inviteStepTitle")}
          onFinish={async () => true}
        >
          {renderWorkflowBody()}
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="passport"
          title={t("compliance.caseDetail.steps.passport")}
          onFinish={async () => true}
        >
          {renderWorkflowBody()}
        </StepsForm.StepForm>

        <StepsForm.StepForm
          name="share"
          title={t("compliance.newCase.workflow.shareStepTitle")}
          onFinish={async () => true}
        >
          {renderWorkflowBody()}
        </StepsForm.StepForm>
      </StepsForm>
    </Card>
  );
}
